"use client";

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Table from "../../../../components/Table";
import Button from "../../../../components/Button";
import Pagination from "../../../../components/Pagination";
import FilterButtons from "../../../../components/universal-filter-tabs";
import Dropdown from "../../../../components/Dropdown"; // ✅ use your custom dropdown
// import styles from "../css/scriptHistory.module.css";
import styles from "../css/medicaHistoryDetails.module.css";
import { fetchProfileInfo, fetchProfileFamily } from "../../../../api/dispensary/script/processScriptApi";
import { fetchMedicalHistory } from "../../../../api/medicalHistory/medicalHistoryApi";
import { useAuth } from "../../../../context/AuthContext";

export default function MedicalHistoryDetailsView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { profileNo, patientLabel } = location.state || {};
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState("Prescriptions");
  const [MedicalAidProvider, setMedicalAidProvider] = useState("");
  const [MedicalAidId, setMedicalAidId] = useState("");

  // Patient dropdown state
  const [patientName, setPatientName] = useState("");
  const [patientList, setPatientList] = useState([]); // labels for dropdown
  const [patientMap, setPatientMap] = useState({}); // label -> id mapping
  const [selectedPatientId, setSelectedPatientId] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const rowsPerPage = 5;

  useEffect(() => {
    let active = true;
    (async () => {
      if (!profileNo) return;
      try {
        const info = await fetchProfileInfo(profileNo);
        if (active && info) {
          setMedicalAidProvider(info.medicalAidProvider || "");
          setMedicalAidId(info.medicalAidNumber || "");
        }
      } catch (e) {
        console.error("Failed to fetch profile info", e);
      }
      try {
        const family = await fetchProfileFamily(profileNo);
        if (active) {
          const labels = (family || []).map((m) => m.label);
          const map = Object.fromEntries((family || []).map((m) => [m.label, m.id]));
          setPatientMap(map);
          setPatientList(labels);
          const initialLabel = patientLabel || labels[0] || "";
          setPatientName(initialLabel);
          setSelectedPatientId(map[initialLabel] || null);
        }
      } catch (e) {
        console.error("Failed to fetch profile family", e);
      }
    })();
    return () => {
      active = false;
    };
  }, [profileNo, patientLabel]);

  // Fetch medical history when patient/filter changes
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!selectedPatientId) return;
      setLoading(true);
      setError("");
      try {
        const type =
          filter === "Prescriptions" ? "process" : filter === "OTC" ? "otc" : filter === "Clinic" ? "clinic" : undefined;
        const data = await fetchMedicalHistory(selectedPatientId, type, user?.branchcode);
        if (alive) setRows(data);
      } catch (e) {
        if (alive) setError(e?.message || "Failed to load history");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [selectedPatientId, filter, user?.branchcode]);

  // Calculate paginated data
  const totalPages = Math.ceil(rows.length / rowsPerPage) || 1;
  const paginatedData = rows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Define table columns based on filter
  const columns =
    filter === "OTC"
      ? [
          { header: "Date", accessor: "date" },
          { header: "Drug(s)", accessor: "drugs" },
          { header: "Total", accessor: "total" },
          { header: "Payment Type", accessor: "paymentType" },
          { header: "OTC Reference", accessor: "otcReference" },
          {
            header: "Action",
            accessor: "action",
            render: (_, row) => (
              <div className={styles.actionsRow}>
                <Button
                  onClick={() =>
                    navigate(
                      "/medical-history/medical-history-details/otc-history-details",
                      { state: { scriptData: row } }
                    )
                  }
                  variant="primary"
                >
                  View
                </Button>
              </div>
            ),
          },
        ]
      : filter === "Clinic"
      ? [
          { header: "Date Visited", accessor: "date" },
          { header: "Consultation Type", accessor: "consultationType" },
          { header: "Clinic Reference", accessor: "clinicReference" },
          {
            header: "Action",
            accessor: "action",
            render: (_, row) => (
              <div className={styles.actionsRow}>
                <Button
                  onClick={() =>
                    navigate(
                      "/medical-history/medical-history-details/clinic-history-details",
                      { state: { scriptData: row } }
                    )
                  }
                  variant="primary"
                >
                  View
                </Button>
              </div>
            ),
          },
        ]
      : [
          { header: "Script #", accessor: "scriptNumber" },
          { header: "Date", accessor: "date" },
          { header: "Drug(s)", accessor: "drugs" },
          {
            header: "Repeat",
            accessor: "repeat",
            render: (_, row) => row.repeatDisplay || row.repeat || `${Math.max((row.repeatsTotal || 0) - (row.repeatsDueCount || 0), 0)} of ${row.repeatsTotal || 0}`,
          },
          { header: "Total", accessor: "total" },
          { header: "Payment Type", accessor: "paymentType" },
          { header: "Script Status", accessor: "scriptStatus" },
          {
            header: "Action",
            accessor: "action",
            render: (_, row) => (
              <div className={styles.actionsRow}>
                <Button
                  onClick={() =>
                    navigate(
                      "/medical-history/medical-history-details/script-history-details",
                      { state: { scriptData: row } }
                    )
                  }
                  variant="primary"
                >
                  View
                </Button>
              </div>
            ),
          },
        ];

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar />

        <main className={styles.content}>
          <div className={styles.topControls}>
            <div>
              <label className={styles.textboxLabel}>Medical Aid Provider</label>
              <input
                type="text"
                placeholder="Medical aid provider"
                value={MedicalAidProvider}
                readOnly
                className={styles.textbox}
              />
            </div>
            <div>
              <label className={styles.textboxLabel}>Medical Aid Number</label>
              <input
                type="text"
                placeholder="Medical aid ID"
                value={MedicalAidId}
                readOnly
                className={styles.textbox}
              />
            </div>
            <div>
              <label className={styles.textboxLabel}>Patient</label>
              <Dropdown
                options={patientList}
                value={patientName}
                onChange={(label) => {
                  setPatientName(label);
                  setSelectedPatientId(patientMap[label] || null);
                  setCurrentPage(1);
                }}
                className={styles.dropdown}
              />
            </div>
          </div>

          <div className={styles.spacerSmall} />

          <div className={styles.tabsWrapper}>
            <FilterButtons
              buttons={["Prescriptions", "OTC", "Clinic"]}
              activeButton={filter}
              onButtonClick={setFilter}
            />
          </div>

          {error && <div className={styles.error}>{error}</div>}
          <Table columns={columns} data={paginatedData} />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </main>

        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>
    </div>
  );
}
